import { ReactSVG } from "react-svg";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useState } from "react";
import { toast } from "react-toastify";
import { FaSpinner } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Helmet } from "react-helmet-async";

type Props = {};

const RegisterNew = (props: Props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const submitForm = async (values: any) => {
    if (!email) return toast.error(t("register.email_required"));
    localStorage.removeItem("parent_token");
    localStorage.removeItem("parent_user");
    localStorage.removeItem("selectedStudent");

    setLoading(true);
    axios
      .post(
        `${process.env.REACT_APP_API_BASE_URL}/user/register`,
        {
          name: values.username,
          ic_number: values.ic,
          email: email.trim(),
          password: values.password,
          confirm_password: values.password,
          phone: values.mobileNumber,
          role: "parent",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          withCredentials: true,
        },
      )
      .then((res) => {
        setLoading(false);
        console.log("res", res);
        if (res.status == 200) {
          // login user
          localStorage.setItem("parent_user", JSON.stringify(res.data.data));
          localStorage.setItem("parent_token", res.data.token);
          navigate("/profile");
        } else {
          toast.error("Error creating account");
        }
      })
      .catch((err) => {
        setLoading(false);
        // console.log("err", err && err.response)

        // Try to extract a useful message from various possible error shapes
        const resp = err && err.response && err.response.data;
        let message = "Error creating account";
        try {
          if (resp) {
            if (typeof resp === "string") {
              message = resp;
            } else if (resp.error && typeof resp.error === "string") {
              message = resp.error;
            } else if (typeof resp === "object") {
              // resp might be like { phone: ["..."], email: [...] }
              const parts: string[] = [];
              Object.values(resp).forEach((v: any) => {
                if (Array.isArray(v)) parts.push(v.join(", "));
                else if (typeof v === "string") parts.push(v);
                else parts.push(JSON.stringify(v));
              });
              if (parts.length > 0) message = parts.join(" / ");
              else message = JSON.stringify(resp);
            }
          }
        } catch (e) {
          console.log("Error parsing error response", e);
        }
        toast.error(message);
      });
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required(t("validation.required")),
    ic: Yup.string()
      .matches(/^[a-zA-Z0-9]+$/, t("validation.invalidIC"))
      .required(t("validation.required")),
    // .matches(/^\d{12}$/, t("validation.invalidIC"))
    // .required(t("validation.required")),
    mobileNumber: Yup.string()
      .matches(/^\d{10,12}$/, t("validation.invalidPhone"))
      .required(t("validation.required")),
    password: Yup.string()
      .min(6, t("validation.password_min"))
      .required(t("validation.required")),
    terms: Yup.boolean().oneOf([true], t("validation.accept_tnc")),
  });

  return (
    <>
      <Helmet>
        <title>Sign Up for MyKoku | Free Smart School Platform Malaysia</title>
        <meta
          name="description"
          content="Create your free MyKoku account today. Used by Malaysian schools to manage attendance, learning & payment. SK, SMK, SJKC & SMJK supported."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="pt-4 px-4 pb-12">
        <div className="mb-3">
          <div>
            <img
              src={process.env.PUBLIC_URL + "/assets/img/koku.png"}
              className="w-20"
              alt=""
            />
            <h3 className="text-2xl font-bold text-black mt-2">
              {t("register_new.register_new")}
            </h3>
          </div>
        </div>

        <div>
          <Formik
            initialValues={{
              username: "",
              ic: "",
              emailInput: "",
              mobileNumber: "",
              password: "",
              terms: false,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              submitForm(values).finally(() => {
                setSubmitting(false);
              });
            }}
          >
            {({ isSubmitting, setFieldValue }) => (
              <Form className="px-2 py-4 ">
                {/* Username */}
                <div className="mb-7">
                  <label className="text-sm font-medium text-black block mb-2">
                    {t("register_new.name")}
                  </label>
                  <Field
                    type="text"
                    name="username"
                    placeholder={t("register_new.enter_name")}
                    className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                  />
                  <ErrorMessage
                    name="username"
                    component="div"
                    className="mt-1 text-red-500 text-sm"
                  />
                </div>

                {/* IC */}
                <div className="mb-7">
                  <label className="text-sm font-medium text-black block mb-2">
                    {t("register_new.ic")}
                  </label>
                  <Field
                    type="text"
                    name="ic"
                    placeholder={t("register_new.enter_ic")}
                    className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                    onCompositionEnd={(e: any) => {
                      const numericValue = e.target.value.replace(/\D/g, "");
                      setFieldValue("ic", numericValue, true);
                    }}
                  />
                  <ErrorMessage
                    name="ic"
                    component="div"
                    className="mt-1 text-red-500 text-sm"
                  />
                </div>

                {/* Email (disabled) */}
                <div className="mb-7">
                  <label className="text-sm font-medium text-black block mb-2">
                    {t("register_new.email")}
                  </label>
                  <input
                    value={email || ""}
                    disabled
                    type="text"
                    placeholder={t("register_new.enter_email")}
                    className="w-full text-lg text-gray-500 border-b-2 border-gray-300 bg-gray-100 cursor-not-allowed py-2"
                  />
                </div>

                {/* Mobile number */}
                <div className="mb-7">
                  <label className="text-sm font-medium text-black block mb-2">
                    {t("register_new.mobile_number")}
                  </label>
                  <Field
                    type="text"
                    name="mobileNumber"
                    placeholder={t("register_new.enter_mobile_number")}
                    className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                    onCompositionEnd={(e: any) => {
                      const numericValue = e.target.value.replace(/\D/g, "");
                      setFieldValue("mobileNumber", numericValue, true);
                    }}
                  />
                  <ErrorMessage
                    name="mobileNumber"
                    component="div"
                    className="mt-1 text-red-500 text-sm"
                  />
                </div>

                {/* Password */}
                <div className="mb-7">
                  <label className="text-sm font-medium text-black block mb-2">
                    {t("register_new.password")}
                  </label>
                  <Field
                    type="password"
                    name="password"
                    placeholder={t("register_new.password")}
                    className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                  />
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="mt-1 text-red-500 text-sm"
                  />
                </div>

                {/* Terms and Conditions */}
                <div className="space-y-2 mb-10">
                  <label className="flex items-start space-x-2 text-sm">
                    <Field
                      type="checkbox"
                      name="terms"
                      className="mt-1 w-4 h-4 text-theme focus:ring-theme rounded"
                    />
                    <span className="text-gray-700">
                      {t("register_new.agree_text")}{" "}
                      <a
                        href="https://mykokurikulum.com/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-theme underline hover:text-theme/80 transition"
                      >
                        {t("register_new.terms_and_conditions")}
                      </a>
                    </span>
                  </label>
                  <ErrorMessage
                    name="terms"
                    component="div"
                    className="text-red-500 text-xs"
                  />
                </div>

                {/* Already have account */}
                <div className="mb-6 text-sm text-gray-500">
                  {t("register_new.already_have_account")}{" "}
                  <Link to="/login" className="text-theme">
                    {t("register_new.sign_in_now")}
                  </Link>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="text-white text-[16px] leading-none w-full py-[15px] bg-theme border-none rounded-3xl flex items-center justify-center disabled:bg-gray-300"
                  disabled={isSubmitting || loading}
                >
                  {loading ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    t("register_new.create_parent_account")
                  )}
                </button>

                {/* Language selector */}
                <div className="grid w-1/2 mt-4 px-2 float-right">
                  <select
                    className="px-2 py-1 border rounded-full text-md bg-white focus:outline-none focus:ring-2 focus:ring-theme appearance-none"
                    onChange={(e) => {
                      i18next.changeLanguage(e.target.value);
                    }}
                  >
                    <option value="-1" disabled selected>
                      -- Language --
                    </option>
                    <option value="en">English</option>
                    <option value="zh">Mandarin</option>
                    <option value="ms">Bahasa</option>
                  </select>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default RegisterNew;
